import React, { useState, useEffect } from 'react';
import { useApi, Spinner, ErrBanner } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import './RoleManagement.css';

/**
 * Admin-only page. Shows role list on the left, permission matrix on the right.
 * Admin role is read-only (system role).
 */
export default function RoleManagement() {
  const { canAccess } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data: roles, loading: loadingRoles, reload: reloadRoles } = useApi(
    () => api.roles.list(), []
  );
  const { data: catalog, loading: loadingCatalog } = useApi(
    () => api.roles.catalog(), [], { modules: [] }
  );

  if (!canAccess('roles')) {
    return (
      <div className="rm-page">
        <ErrBanner msg="Access Denied: You do not have permission to manage roles." />
      </div>
    );
  }

  const onCreate = () => setSelectedRole({ isNew: true, role_name: '', description: '', permissions: {} });

  return (
    <div className="rm-page">
      {msg && <div className="rm-toast">{msg}</div>}

      <div className="rm-layout">
        <div className="rm-sidebar section-card">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Roles
            <button className="btn btn-primary btn-xs" style={{ padding: '2px 8px' }} onClick={onCreate}>＋ New</button>
          </div>
          {loadingRoles ? <Spinner /> : (
            <div className="rm-role-list">
              {(roles || []).length === 0 ? (
                <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 12 }}>
                  No roles yet. Click "+ New Role".
                </div>
              ) : (roles || []).map(r => (
                <div
                  key={r.id}
                  className={`rm-role-item ${selectedRole?.id === r.id ? 'active' : ''}`}
                  onClick={() => setSelectedRole(r)}
                >
                  <div>
                    <div className="rm-role-name">
                      {r.role_name}
                      {Number(r.is_system) === 1 && <span className="rm-system-pill">SYSTEM</span>}
                    </div>
                    <div className="rm-role-meta">
                      {r.user_count} user{Number(r.user_count) !== 1 ? 's' : ''}
                      {r.description && ` · ${r.description}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rm-editor">
          {loadingCatalog ? <Spinner /> :
            selectedRole ? (
              <RoleEditor
                role={selectedRole}
                catalog={catalog.modules || []}
                onSaved={() => { reloadRoles(); setSelectedRole(null); notify('✅ Role saved'); }}
                onDeleted={() => { reloadRoles(); setSelectedRole(null); notify('✅ Role deleted'); }}
                onCancel={() => setSelectedRole(null)}
                onError={(m) => notify('❌ ' + m)}
              />
            ) : (
              <div className="rm-empty">
                ← Pick a role to edit its permissions, or click "+ New Role" above.
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Role editor — name/desc + permission matrix
═══════════════════════════════════════════════════════════ */
function RoleEditor({ role, catalog, onSaved, onDeleted, onCancel, onError }) {
  const isNew = !!role.isNew;
  const isSystem = Number(role.is_system) === 1;
  const [form, setForm] = useState({
    role_name: role.role_name || '',
    description: role.description || '',
    permissions: {},
  });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);

  // Load full permissions when existing role is selected
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isNew) {
        setForm({ role_name: role.role_name, description: role.description, permissions: {} });
        return;
      }
      try {
        const full = await api.roles.fetch(role.id);
        if (!cancelled) {
          setForm({
            role_name: full.role_name,
            description: full.description || '',
            permissions: full.permissions || {},
          });
        }
      } catch (e) {
        onError(e.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [role.id, isNew]);

  const togglePerm = (module, action) => {
    if (isSystem) return;
    setForm(f => {
      const m = { ...(f.permissions[module] || {}) };
      m[action] = !m[action];
      return { ...f, permissions: { ...f.permissions, [module]: m } };
    });
  };

  const toggleAllInModule = (module, actions, turnOn) => {
    if (isSystem) return;
    setForm(f => {
      const m = { ...(f.permissions[module] || {}) };
      actions.forEach(a => { m[a] = turnOn; });
      return { ...f, permissions: { ...f.permissions, [module]: m } };
    });
  };

  const handleSave = async () => {
    if (!form.role_name.trim()) { onError('Role name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.roles.create(form);
      } else {
        await api.roles.update(role.id, form);
      }
      onSaved();
    } catch (e) {
      onError(e.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.roles.remove(role.id);
      onDeleted();
    } catch (e) {
      onError(e.message);
    }
    setConfirm(false);
  };

  return (
    <div className="section-card rm-editor-card">
      <div className="section-header">
        {isNew ? 'Create Role' : `Edit Role — ${role.role_name}`}
        {isSystem && <span className="rm-system-pill" style={{ marginLeft: 8 }}>SYSTEM (read-only)</span>}
      </div>

      <div className="rm-editor-body">
        <div className="form-row" style={{ padding: '0 0 12px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <div className="form-group">
            <label>Role Name *</label>
            <input
              value={form.role_name}
              disabled={isSystem}
              onChange={e => setForm({ ...form, role_name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <input
              value={form.description}
              disabled={isSystem}
              onChange={e => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>

        <div className="rm-matrix-wrap">
          <table className="rm-matrix">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Module</th>
                <th>Create</th><th>Read</th><th>Update</th><th>Delete</th><th>Approve</th>
                <th>All</th>
              </tr>
            </thead>
            <tbody>
              {catalog.map(m => (
                <tr key={m.module_name}>
                  <td style={{ textAlign: 'left', fontWeight: 600 }}>
                    {MODULE_LABELS[m.module_name] || prettyName(m.module_name)}
                    {MODULE_HINTS[m.module_name] && (
                      <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)', marginTop: 2 }}>
                        {MODULE_HINTS[m.module_name]}
                      </div>
                    )}
                  </td>
                  {['create', 'read', 'update', 'delete', 'approve'].map(act => (
                    <td key={act}>
                      {m.actions.includes(act) ? (
                        <input
                          type="checkbox"
                          disabled={isSystem}
                          checked={!!form.permissions[m.module_name]?.[act]}
                          onChange={() => togglePerm(m.module_name, act)}
                        />
                      ) : <span className="rm-na">—</span>}
                    </td>
                  ))}
                  <td>
                    {!isSystem && (
                      <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <button className="rm-miniBtn" title="All on"
                          onClick={() => toggleAllInModule(m.module_name, m.actions, true)}>✓</button>
                        <button className="rm-miniBtn" title="All off"
                          onClick={() => toggleAllInModule(m.module_name, m.actions, false)}>✕</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-actions">
        <button className="btn btn-sm" onClick={onCancel}>Close</button>
        {!isSystem && !isNew && (
          <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
            onClick={() => setConfirm(true)}>🗑 Delete</button>
        )}
        {!isSystem && (
          <button className="btn btn-primary btn-sm"
            onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving...' : (isNew ? 'Create' : 'Save Changes')}
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={confirm}
        title="Delete Role?"
        message={`Delete role "${role.role_name}"? Users with this role must be reassigned first.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(false)}
        confirmLabel="Delete"
      />
    </div>
  );
}

/* Friendly module names + action-mapping hints. The permission grid uses the
 * fixed columns Create/Read/Update/Delete/Approve, so for modules whose
 * operations don't map 1:1 we show a hint explaining the mapping. */
const MODULE_LABELS = {
  reconciliation: 'Day End Reconciliation',
};
const MODULE_HINTS = {
  reconciliation: '',
  employees: '',
};

/* ── Pretty-print module names (dashboard → Dashboard, lube_sales → Lube Sales) ── */
function prettyName(raw) {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
